<template>
  <div class="page-wrapper mt-3" style="width: 100%">
    <PageTitle title="Личный кабинет студента: Сдача работ" />

    <!-- ШАГ 1: ВЫБОР АКТИВНОГО КУРСА -->
    <v-card variant="outlined" class="ma-2 pa-4 bg-white rounded-lg">
      <v-row align="center" no-gutters>
        <v-col cols="12" sm="9" class="pr-sm-4">
          <v-select
            v-model="selectedCourseId"
            :items="myCourses"
            item-title="courseName"
            item-value="id"
            label="Выберите учебный курс для прохождения"
            placeholder="Загрузка доступных курсов..."
            variant="outlined"
            density="compact"
            hide-details
            @update:model-value="loadCourseDetails"
          ></v-select>
        </v-col>

        <v-col cols="12" sm="3" class="text-sm-right mt-3 mt-sm-0">
          <v-chip
            v-if="courseDetails"
            color="success"
            variant="tonal"
            size="small"
            class="font-weight-bold px-3 py-3"
            prepend-icon="mdi-check-decagram-outline"
          >
            Курс активен
          </v-chip>
        </v-col>
      </v-row>
    </v-card>

    <!-- СПИСОК МОДУЛЕЙ И ФОРМА СДАЧИ ЭССЕ -->
    <v-row v-if="courseDetails" class="ma-1 mt-3">

      <!-- ЛЕВАЯ КОЛОНКА: Компактная структура курса -->
      <v-col cols="12" md="5">
        <v-card variant="outlined" class="pa-4 bg-white h-100">
          <span class="text-subtitle-1 font-weight-bold d-block mb-3 text-secondary">
            Учебная структура курса:
          </span>

          <v-list class="pa-0 border rounded" density="compact">
            <!-- УРОВЕНЬ 1: МОДУЛИ -->
            <v-list-group v-for="mod in courseDetails.modules" :key="'mod-' + mod.id" :value="'mod-' + mod.id">
              <template v-slot:activator="{ props }">
                <v-list-item
                  v-bind="props"
                  prepend-icon="mdi-folder-text-outline"
                  class="bg-grey-lighten-5"
                >
                  <v-list-item-title class="font-weight-bold text-body-2">{{ mod.courseModuleName }}</v-list-item-title>
                </v-list-item>
              </template>

              <v-list-item v-if="!mod.subjects || mod.subjects.length === 0" class="pl-8 text-caption text-grey">
                Нет предметов
              </v-list-item>

              <!-- УРОВЕНЬ 2: ПРЕДМЕТЫ -->
              <v-list-group v-for="sub in mod.subjects" :key="'sub-' + sub.id" :value="'sub-' + sub.id">
                <template v-slot:activator="{ props }">
                  <v-list-item
                    v-bind="props"
                    prepend-icon="mdi-book-open-variant"
                    class="pl-6 text-blue-darken-3"
                  >
                    <v-list-item-title class="text-body-2">{{ sub.courseModuleSubjectName }}</v-list-item-title>
                  </v-list-item>
                </template>

                <v-list-item v-if="!sub.probes || sub.probes.length === 0" class="pl-14 text-caption text-grey">
                  Нет заданий
                </v-list-item>

                <!-- УРОВЕНЬ 3: ПРОБЫ / ЗАДАНИЯ -->
                <v-list-item
                  v-for="probe in sub.probes"
                  :key="'probe-' + probe.id"
                  :active="activeProbe?.id === probe.id"
                  color="deep-orange-darken-1"
                  class="pl-12 border-bottom"
                  @click="selectProbe(probe, mod, sub)"
                >
                  <template v-slot:prepend>
                    <v-icon size="small" :color="activeProbe?.id === probe.id ? 'deep-orange-darken-1' : 'grey-darken-1'">
                      mdi-file-document-edit-outline
                    </v-icon>
                  </template>

                  <v-list-item-title class="text-body-2 font-weight-medium text-truncate">
                    [{{ probe.probeOrder }}] {{ probe.probeName }}
                  </v-list-item-title>

                  <template v-slot:append>
                    <v-chip v-if="probe.currentEsseStatus === 'CHECKED'" size="x-small" color="success" variant="flat">
                      {{ probe.grade }} б.
                    </v-chip>
                    <v-chip v-else-if="probe.currentEsseStatus === 'PROMPT_INJECTION_DETECTED'" size="x-small" color="purple-darken-2" variant="flat" class="font-weight-bold">
                      Блок ИИ
                    </v-chip>
                    <v-chip v-else-if="probe.currentEsseStatus === 'IN_WORK'" size="x-small" color="info" variant="flat" class="v-chip--loading">
                      ИИ...
                    </v-chip>
                    <v-chip v-else-if="probe.currentEsseStatus === 'DRAFT'" size="x-small" color="orange-darken-2" variant="flat">
                      Черновик
                    </v-chip>
                    <v-chip v-else-if="probe.currentEsseStatus === 'SENT'" size="x-small" color="warning" variant="flat">
                      Очередь
                    </v-chip>
                    <v-chip v-else-if="probe.currentEsseStatus === 'FAIL_CHECK' || probe.currentEsseStatus === 'SYSTEM_ERROR'" size="x-small" color="error" variant="flat">
                      Ошибка
                    </v-chip>
                    <v-chip v-else size="x-small" color="grey-lighten-1" variant="flat" class="text-grey-darken-2">
                      —
                    </v-chip>
                  </template>
                </v-list-item>
              </v-list-group>
            </v-list-group>
          </v-list>
        </v-card>
      </v-col>
           <!-- ПРАВАЯ КОЛОНКА: Рабочая область или просмотр рецензии ИИ -->
                  <v-col cols="12" md="7">
                    <v-card v-if="activeProbe" variant="outlined" class="pa-4 bg-white h-100">

                      <!-- Контекст и дедлайны -->
                      <div class="text-caption text-grey mb-1">
                        {{ activeModuleContext?.courseModuleName }} / {{ activeSubjectContext?.courseModuleSubjectName }}
                      </div>
                      <div class="text-right">
                        <span v-if="activeSubjectContext?.startTime || activeSubjectContext?.endTime" class="text-caption text-grey d-block">
                          Период темы: {{ formatDateTime(activeSubjectContext.startTime) }} — {{ formatDateTime(activeSubjectContext.endTime) }}
                        </span>
                        <v-chip
                          v-if="activeProbe.endTime"
                          color="red-darken-1"
                          variant="tonal"
                          size="x-small"
                          class="font-weight-bold mt-1"
                          prepend-icon="mdi-clock-alert-outline"
                        >
                          Сдать до: {{ formatDateTime(activeProbe.endTime) }}
                        </v-chip>
                      </div>

                      <!-- НАЗВАНИЕ ЗАЗАНИЯ -->
                      <div class="d-flex justify-space-between align-center mb-2">
                        <span class="text-h6 font-weight-bold text-deep-orange-darken-1">
                          <v-icon color="deep-orange-darken-1" class="mr-1">mdi-file-document-outline</v-icon>
                          Задание №{{ activeProbe.probeOrder }}: {{ activeProbe.probeName }}
                        </span>
                      </div>

                      <!-- ОПИСАНИЕ И ИНСТРУКЦИЯ -->
                      <p class="text-body-2 text-grey-darken-3 mb-4 font-italic bg-grey-lighten-4 pa-3 rounded border-left-strong">
                        <strong>Описание пробы:</strong> {{ activeProbe.probeDescription || 'Инструкция к написанию эссе отсутствует.' }}
                      </p>

                      <!-- ========================================================================= -->
                      <!-- ИСПРАВЛЕНО: СЕЛЕКТОР ИСТОРИИ ПЕРЕНЕСЕН В САМЫЙ ВЕРХ И ВИДЕН ВСЕГДА -->
                      <!-- ========================================================================= -->
                      <div v-if="activeProbe.attempts && activeProbe.attempts.length > 1" class="mb-4 bg-grey-lighten-4 pa-2 rounded border">
                        <v-select
                          v-model="selectedAttempt"
                          :items="activeProbe.attempts"
                          item-title="attempt"
                          return-object
                          label="История ваших попыток сдачи:"
                          density="compact"
                          variant="solo"
                          flat
                          hide-details
                        >
                          <template v-slot:item="{ props, item }">
                            <v-list-item
                              v-bind="props"
                              :title="'Попытка №' + item.raw.attempt + ' — Оценка: ' + (item.raw.grade ?? 0) + ' б.'"
                              :subtitle="formatDateTime(item.raw.createdAt)"
                            ></v-list-item>
                          </template>
                          <template v-slot:selection="{ item }">
                            <span class="text-body-2 font-weight-bold text-blue-darken-3">
                              <v-icon start size="small" color="blue-darken-3">mdi-history</v-icon>
                              Выбрана Попытка №{{ item.raw.attempt }}
                              <span v-if="item.raw.status === 'CHECKED'"> (Оценка: {{ item.raw.grade ?? 0 }} б.)</span>
                              <span v-else-if="item.raw.status === 'DRAFT'"> (Черновик)</span>
                              <span v-else-if="item.raw.status === 'SENT' || item.raw.status === 'IN_WORK'"> (На проверке ИИ)</span>
                            </span>
                          </template>
                        </v-select>
                      </div>

                      <v-divider class="my-3"></v-divider>

                      <!-- ДИНАМИЧЕСКИЙ ВЫВОД РАБОЧЕЙ ОБЛАСТИ НА ОСНОВЕ СТАТУСА ВЫБРАННОЙ ПОПЫТКИ -->
                      <!-- Теперь экраны переключаются в зависимости от выбранного элемента истории! -->

                      <!-- СЦЕНАРИЙ 1: Студент пишет черновик или начинает с нуля -->
                      <div v-if="!selectedAttempt || selectedAttempt.status === 'NOT_STARTED' || selectedAttempt.status === 'DRAFT' || activeProbe.currentEsseStatus === 'DRAFT'">
                        <v-textarea
                          v-model="submissionBody"
                          label="Вставьте или напишите текст вашего эссе здесь*"
                          rows="15"
                          variant="outlined"
                          counter
                          placeholder="Рекомендуемый объем от 200 слов. Черновик сохраняется автоматически по мере ввода..."
                        ></v-textarea>

                        <div class="d-flex justify-space-between align-center mt-3">
                          <span class="text-caption text-grey-darken-1">
                            <v-icon start size="small" color="grey">mdi-cloud-check-outline</v-icon> Автосохранение включено (Попытка №{{ selectedAttempt?.attempt || 1 }})
                          </span>

                          <v-btn
                            :disabled="!submissionBody || submissionBody.trim().length < 200"
                            color="success"
                            prepend-icon="mdi-send-outline"
                            size="large"
                            @click="submitEssayToServer"
                          >
                            Отправить работу
                          </v-btn>
                        </div>
                      </div>

                      <!-- СЦЕНАРИЙ 2: Выбранная попытка в очереди или проверяется прямо сейчас -->
                      <div v-else-if="selectedAttempt.status === 'SENT' || selectedAttempt.status === 'IN_WORK'" class="text-center py-10">
                        <v-progress-circular indeterminate color="deep-orange-darken-1" size="64" class="mb-4"></v-progress-circular>
                        <h3 class="text-h6 font-weight-bold">Попытка №{{ selectedAttempt.attempt }} проверяется ИИ!</h3>
                        <p class="text-body-2 text-grey mt-1">Результаты и детальный разбор появятся здесь в течение 1–2 минут. Обновите страницу позже.</p>
                      </div>

                      <!-- СЦЕНАРИЙ 3: Выбранная попытка заблокирована за Prompt Injection -->
                      <div v-else-if="selectedAttempt.status === 'PROMPT_INJECTION_DETECTED'" class="text-center py-10">
                        <v-icon size="64" color="purple-darken-2" class="mb-4">mdi-shield-alert-outline</v-icon>
                        <h3 class="text-h6 font-weight-bold text-purple-darken-3">Попытка №{{ selectedAttempt.attempt }} заблокирована!</h3>
                        <p class="text-body-2 text-grey mt-1">В тексте обнаружена попытка взлома ИИ. Выставлено 0 баллов.</p>
                      </div>

                      <!-- СЦЕНАРИЙ 4: Техническая ошибка выбранной попытки -->
                      <div v-else-if="selectedAttempt.status === 'SYSTEM_ERROR' || selectedAttempt.status === 'FAIL_CHECK'" class="text-center py-10">
                        <v-icon size="64" color="error" class="mb-4">mdi-alert-circle-outline</v-icon>
                        <h3 class="text-h6 font-weight-bold">При обработке Попытки №{{ selectedAttempt.attempt }} возникла ошибка</h3>
                        <p class="text-body-2 text-grey mt-1">Система перепроверит работу автоматически. Обновите страницу позже.</p>
                      </div>

                      <!-- СЦЕНАРИЙ 5: Выбранная попытка успешно проверена ИИ -->
                      <div v-else-if="selectedAttempt.status === 'CHECKED'">
                        <div class="d-flex justify-space-between align-center bg-green-lighten-5 pa-3 rounded mb-6">
                          <span class="text-subtitle-1 font-weight-bold text-success-darken-3">
                            <v-icon start color="success">mdi-check-decagram</v-icon> Результаты Попытки №{{ selectedAttempt.attempt }}
                          </span>
                          <span class="text-h5 font-weight-bold text-success">{{ selectedAttempt.grade }} б.</span>
                        </div>
                          <!-- ========================================================================= -->
                                      <!-- ДОБАВЛЕНО: РАСКРЫВАЮЩИЙСЯ БЛОК С ТЕКСТОМ ОТПРАВЛЕННОГО ЭССЕ -->
                                      <!-- ========================================================================= -->
                                      <v-expansion-panels class="mb-5 border rounded" variant="accordion">
                                        <v-expansion-panel elevation="0" class="bg-grey-lighten-5">
                                          <v-expansion-panel-title class="py-2 text-body-2 font-weight-medium text-secondary">
                                            <v-icon start size="small" color="secondary">mdi-text-box-search-outline</v-icon>
                                            Показать текст эссе этой попытки
                                          </v-expansion-panel-title>
                                          <v-expansion-panel-text class="pt-2 text-body-2 line-height-relaxed text-grey-darken-4">
                                            <!-- white-space: pre-wrap сохраняет все абзацы студента -->
                                            <div style="white-space: pre-wrap; max-height: 300px; overflow-y: auto;" class="pa-1">
                                              {{ selectedAttempt.body || 'Текст работы пуст.' }}
                                            </div>
                                          </v-expansion-panel-text>
                                        </v-expansion-panel>
                                      </v-expansion-panels>


                        <!-- ДВЕ КНОПКИ ДЕЙСТВИЯ -->
                        <v-row class="ma-0">
                          <v-col cols="12" sm="6" class="pa-1">
                            <v-btn
                              variant="outlined"
                              color="secondary"
                              class="w-100 py-3"
                              prepend-icon="mdi-eye-text-outline"
                              @click="openStudentReviewModal"
                            >
                              Посмотреть рецензию ИИ
                            </v-btn>
                          </v-col>

                          <v-col cols="12" sm="6" class="pa-1">
                            <!-- Кнопку новой попытки показываем только если мы смотрим САМУЮ СВЕЖУЮ попытку в списке -->
                            <v-btn
                              v-if="selectedAttempt && activeProbe.attempts && selectedAttempt.id === activeProbe.attempts[0]?.id"
                              variant="flat"
                              color="warning"
                              class="w-100 py-3 font-weight-bold"
                              prepend-icon="mdi-pencil-plus-outline"
                              @click="startNewAttemptSubmission"
                            >
                              Написать еще один вариант
                            </v-btn>
                          </v-col>
                        </v-row>
                      </div>
                    </v-card>

                    <!-- Заглушка, если задание еще не выбрано -->
                    <v-card v-else variant="outlined" class="d-flex align-center justify-center text-center pa-10 bg-white h-100 text-grey-lighten-1">
                      <div>
                        <v-icon size="64" class="mb-2">mdi-gesture-tap</v-icon>
                        <p class="text-body-1 font-weight-medium text-grey-darken-1">
                          Выберите задание (пробу) из дерева слева,<br>чтобы приступить к написанию или посмотреть оценку
                        </p>
                      </div>
                    </v-card>
                  </v-col>
          </v-row>

          <!-- МОДАЛКА: Детальный вывод рецензии критериев -->
          <v-dialog v-model="studentReviewDialog" max-width="700px">
            <v-card class="pa-2">
              <v-card-title class="font-weight-bold text-h5 d-flex justify-space-between align-center">
                <span>Рецензия нейросети</span>
                <v-chip color="success" variant="flat">Балл: {{ activeProbe?.grade }}</v-chip>
              </v-card-title>
              <v-card-text style="max-height: 60vh; overflow-y: auto;">
                <div v-if="studentReviewLoading" class="text-center py-6">
                  <v-progress-circular indeterminate color="deep-orange-darken-1"></v-progress-circular>
                </div>
                <div v-else>
                  <div v-for="crit in reviewCriteria" :key="crit.id" class="mb-4 pa-3 border rounded bg-grey-lighten-5">
                    <div class="d-flex justify-space-between mb-1 font-weight-bold text-secondary">
                      <span>Показатель №{{ crit.criteriaOrder }}</span>
                      <span>{{ crit.criteriaRate }} / 3 б.</span>
                    </div>
                    <div style="white-space: pre-wrap;" class="text-body-2 text-grey-darken-3 pl-2 border-left">
                      {{ crit.criteriaRateDescription }}
                    </div>
                  </div>
                </div>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="deep-orange-darken-1" variant="flat" @click="studentReviewDialog = false">Понятно</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>
      </template>
<script setup>
import { ref, onMounted, watch, nextTick } from "vue";
import PageTitle from "@/elements/PageTitle.vue";
import { customAxios } from "@/service/customAxios";
import { usePopupStore } from "@/store/popup";
import { formatDateTime, debounce } from "@/util/common";
// Переменная для хранения выбранной студентом попытки из истории
const selectedAttempt = ref(null);

const { showError } = usePopupStore();
const isFormLoading = ref(false);

// Реактивные переменные
const myCourses = ref([]);
const selectedCourseId = ref(null);
const courseDetails = ref(null);

// Переменные состояния для выбранной пробы и её контекста
const activeProbe = ref(null);
const activeModuleContext = ref(null);
const activeSubjectContext = ref(null);
const submissionBody = ref("");

// Состояние модального окна рецензии ИИ
const studentReviewDialog = ref(false);
const studentReviewLoading = ref(false);
const reviewCriteria = ref([]);

/**
 * 1. Загрузка списка доступных курсов
 */
async function loadStudentCourses() {
  try {
    const r = await customAxios.post("/v1/student/courses/list", {});
    myCourses.value = r.data || [];
  } catch (e) {
    showError(e);
  }
}

/**
 * 2. Загрузка модулей выбранного курса и обновление состояния
 */
async function loadCourseDetails() {
  if (!selectedCourseId.value) {
    courseDetails.value = null;
    activeProbe.value = null;
    activeModuleContext.value = null;
    activeSubjectContext.value = null;
    return;
  }

  try {
    const payload = { courseId: selectedCourseId.value };
    const r = await customAxios.post("/v1/student/courses/details", payload);
    courseDetails.value = r.data;

    // Синхронизируем данные выбранной пробы при обновлении структуры дерева
    if (activeProbe.value && courseDetails.value?.modules) {
      for (const mod of courseDetails.value.modules) {
        if (!mod.subjects) continue;
        for (const sub of mod.subjects) {
          if (!sub.probes) continue;
          const updatedProbe = sub.probes.find(p => p.id === activeProbe.value.id);
          if (updatedProbe) {
            activeProbe.value = updatedProbe;

            activeModuleContext.value = mod;
            activeSubjectContext.value = sub;

            selectProbe(activeProbe.value, activeModuleContext.value, activeSubjectContext.value);

            break;
          }
        }
      }
    }
  } catch (e) {
    showError(e);
  }
}
/**
 * 3. Выбор конкретной пробы в дереве навигации
 */

/**
 * Клик по конкретной пробе в дереве навигации
 */
function selectProbe(probe, mod, sub) {
  isFormLoading.value = true;
  submissionBody.value = "";

  // СИНХРОНИЗАЦИЯ ИМЕН ПОЛЕЙ: Переносим данные из бэкенд-поля attemptDtos в фронтенд-поле attempts
  if (probe.attemptDtos && !probe.attempts) {
    probe.attempts = probe.attemptDtos;
  }

  activeProbe.value = probe;
  activeModuleContext.value = mod;
  activeSubjectContext.value = sub;

  if (!probe.attempts) {
    probe.attempts = [];
  }

  // Сортируем попытки по убыванию (чтобы самая свежая была первой)
  probe.attempts.sort((a, b) => b.attempt - a.attempt);

  // Автоматически выбираем для просмотра самую верхнюю (актуальную) попытку
  if (probe.attempts.length > 0) {
    selectedAttempt.value = probe.attempts[0];
  } else {
    selectedAttempt.value = null;
  }

  nextTick(() => {
    // Подставляем текст выбранной попытки в текстовое поле
    submissionBody.value = selectedAttempt.value ? selectedAttempt.value.body : "";

    nextTick(() => {
      isFormLoading.value = false;
    });
  });
}


/**
 * 4. Воркер автоматического сохранения черновика в фоновом режиме
 */
const debouncedDraftSave = debounce(async () => {
  if (!activeProbe.value) return;

  // Разрешаем отправку пустой строки, если студент полностью очистил текстовое поле
  const currentText = submissionBody.value ? submissionBody.value : "";

  const payload = {
    esseBody: currentText
  };

  try {
    const r = await customAxios.post(`/v1/student/modules/subject/probe/${activeProbe.value.id}/draft`, payload);
    if(r.status===202){
      await loadCourseDetails();
      console.log("Черновик эссе успешно автосохранен в БД");

          // Обновляем текст в кэше локального объекта, чтобы данные не терялись при переходах
          activeProbe.value.currentEsseBody = currentText;

          if (activeProbe.value.currentEsseStatus === 'NOT_STARTED') {
            activeProbe.value.currentEsseStatus = 'DRAFT';

            // Синхронизируем статус внутри дерева структуры курса
            if (courseDetails.value?.modules) {
              for (const mod of courseDetails.value.modules) {
                if (!mod.subjects) continue;
                for (const sub of mod.subjects) {
                  if (!sub.probes) continue;
                  const target = sub.probes.find(p => p.id === activeProbe.value.id);
                  if (target) {
                    target.currentEsseStatus = 'DRAFT';
                    target.currentEsseBody = currentText;
                  }
                }
              }
            }
          }
    }

  } catch (e) {
    console.error("Ошибка автосохранения черновика:", e);
  }
}, 1500);

// Наблюдатель за вводом текста (игнорирует смену задания благодаря флагу)
watch(submissionBody, (newVal, oldVal) => {
  if (isFormLoading.value) return;

  if (newVal !== oldVal) {
    const status = activeProbe.value?.currentEsseStatus;
    if (status === 'NOT_STARTED' || status === 'DRAFT') {
      debouncedDraftSave();
    }
  }
});

/**
 * 5. Финальная отправка по кнопке (Перевод в SENT и запуск ИИ)
 */
async function submitEssayToServer() {
  if (!activeProbe.value || !submissionBody.value.trim()) return;

  const payload = {
    esseBody: submissionBody.value
  };

  try {
    const r = await customAxios.post(`/v1/student/modules/subject/probe/${activeProbe.value.id}/submit`, payload);
    if (r.status === 201 ) {
      await loadCourseDetails();
    }
  } catch (e) {
    showError(e);
  }
}

/**
 * 6. Запрос рецензии ИИ по ID пробы с мягким парсингом структуры
 */
async function openStudentReviewModal() {
  if (!activeProbe.value) return;

  studentReviewDialog.value = true;
  studentReviewLoading.value = true;

  try {
    // 1. Вычисляем ID конкретной выбранной из истории попытки эссе
    const esseId = selectedAttempt.value ? selectedAttempt.value.id : null;

    if (!esseId) {
      console.error("🛑 Ошибка: Не удалось определить ID попытки эссе для загрузки рецензии");
      return;
    }

    // 2. ИСПРАВЛЕНО: Отправляем запрос на эндпоинт конкретного эссе по его esseId
    //const r = await customAxios.post(`/v1/student/probes/${esseId}/review`, {});
    //admin/results/essay
     const r = await customAxios.get(`/v1/admin/results/essay/${esseId}`);

    if (r.status === 200 && r.data) {
      // Безопасный разбор — принимает и чистый массив, и DTO-обертку
      if (Array.isArray(r.data)) {
        reviewCriteria.value = r.data;
      } else {
        reviewCriteria.value = r.data.criteriaList || r.data.criteriaResults || [];
      }
    }
  } catch (e) {
    showError(e);
    reviewCriteria.value = [];
  } finally {
    studentReviewLoading.value = false;
  }
}


/**
 * 7. Инициализация новой попытки сдачи эссе (Создание пустого черновика в БД)
 */
async function startNewAttemptSubmission() {
  if (!activeProbe.value) return;

  const confirmAction = confirm(
    "Вы уверены, что хотите создать новую попытку сдачи? Ваша текущая оценка и рецензия нейросети будут сохранены в истории, а перед Вами откроется чистое поле для написания нового варианта эссе."
  );

  if (!confirmAction) return;

  try {
    const probeId = activeProbe.value.id;

    // 1. Вызываем новый эндпоинт, который создает в базе пустую строку со статусом DRAFT и attempt + 1
    const r = await customAxios.post(`/v1/student/probe/${probeId}/init-new-attempt`, {});

    if (r.status === 200 || r.status === 201) {
      // 2. Сбрасываем текст в форме ввода, так как для новой попытки он должен быть чистым
      submissionBody.value = "";

      // 3. Перезапрашиваем структуру курса с бэкенда
      await loadCourseDetails();

      console.log("Новая попытка успешно инициализирована. Статус пробы изменен на DRAFT.");
    }
  } catch (e) {
    showError(e);
  }
}

watch(selectedAttempt, (newAttempt) => {
  if (newAttempt) {
    // Перезаписываем submissionBody текстом выбранной из истории попытки
    submissionBody.value = newAttempt.body || "";
  }
});

onMounted(async () => {
  await loadStudentCourses();
});
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
.line-height-relaxed {
  line-height: 1.6;
}
.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
.border-bottom:last-child {
  border-bottom: none;
}
.border-left {
  border-left: 3px solid #1867c0;
}

/* Вращение иконки загрузки во время проверки нейросетью */
.v-chip--loading :deep(.v-icon) {
  animation: spin 2s linear infinite;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.border-left-strong {
  border-left: 4px solid #1976D2 !important;
}
</style>


